import { createEnv } from '@t3-oss/env-nextjs';
import { z } from 'zod';

export const env = createEnv({
  client: {
    // URLs
    NEXT_PUBLIC_BOOTCAMP_APPLY_API_URL: z.url(),
    NEXT_PUBLIC_ONLINE_API_URL: z.url(),
    // brand별 홈(메인) URL — 로고 클릭·복귀 시 브랜드 메인으로 이동.
    NEXT_PUBLIC_NBCAMP_URL: z.url(),
    NEXT_PUBLIC_KANT_URL: z.url(),
    NEXT_PUBLIC_CDN_BASE_URL: z.url(),

    // 외부 서비스 키
    NEXT_PUBLIC_CHANNEL_IO_PLUGIN_KEY: z.string(),

    // Google Tag Manager 컨테이너 ID. production Vercel env에만 주입 → 미설정 환경(local·preview)에선
    // GtmScript가 렌더를 건너뛴다(광고 데이터 오염 방지). 따라서 optional.
    NEXT_PUBLIC_GTM_ID: z.string().optional(),

    // cross-platform-logger(CPL) 초기화 키 — Flounder(Kinesis)/Hackle/Braze.
    // initCPLog는 flounder·hackle 인스턴스를 무조건 생성하므로 둘은 필수, braze는 key+endpoint가 둘 다 있을 때만 init.
    NEXT_PUBLIC_FLOUNDER_KEY: z.string(),
    NEXT_PUBLIC_AMPLITUDE_KEY: z.string(),
    NEXT_PUBLIC_HACKLE_SDK_KEY: z.string(),
    NEXT_PUBLIC_BRAZE_API_KEY: z.string(),
    NEXT_PUBLIC_BRAZE_SDK_ENDPOINT: z.string(),

    // 채널톡 커스텀 버튼 이미지 (PC/Mobile)
    NEXT_PUBLIC_CHTALK_PC: z.url(),
    NEXT_PUBLIC_CHTALK_MO: z.url(),

    // 세일즈 랜딩 썸네일 CloudFront 호스트(next/image 허용 도메인). env별로 다르고, 미설정 시
    // next.config가 *.cloudfront.net 와일드카드로 폴백하므로 optional. Vercel 환경별로 박으면 고정됨.
    NEXT_PUBLIC_CLOUDFRONT_HOSTNAME: z.string().optional(),

    // Datadog RUM — RUM Application 생성 후 값 주입. 미설정 환경(local·preview)에선
    // instrumentation-client가 init을 건너뛰므로 모두 optional. env는 'prod' | 'test' 등.
    // (실제 init은 apps/apply-frontend/instrumentation-client.ts가 process.env로 읽어 수행)
    NEXT_PUBLIC_DATADOG_APPLICATION_ID: z.string().optional(),
    NEXT_PUBLIC_DATADOG_CLIENT_TOKEN: z.string().optional(),
    NEXT_PUBLIC_DATADOG_SERVICE: z.string().optional(),
    NEXT_PUBLIC_DATADOG_ENV: z.string().optional(),
    NEXT_PUBLIC_DATADOG_SESSION_RATE: z.string().optional(),
    NEXT_PUBLIC_DATADOG_SESSION_REPLAY_RATE: z.string().optional(),
    NEXT_PUBLIC_DATADOG_TRACE_SAMPLE_RATE: z.string().optional(),
  },
  server: {
    // API Routes에서 사용
    GITHUB_PUBLIC_API_TOKEN: z.string().optional(),

    // proxy.ts에서 utm-collector 백엔드 인증에 사용. server-only —
    // NEXT_PUBLIC_ 접두사를 붙이면 클라이언트 번들에 노출되므로 금지.
    // 미설정 환경(local·preview)에선 UTM 캡처를 건너뛰므로 optional.
    UTM_COLLECTOR_AUTH_TOKEN: z.string().optional(),
  },
  runtimeEnv: {
    // Client - URLs
    NEXT_PUBLIC_BOOTCAMP_APPLY_API_URL: process.env.NEXT_PUBLIC_BOOTCAMP_APPLY_API_URL,
    NEXT_PUBLIC_ONLINE_API_URL: process.env.NEXT_PUBLIC_ONLINE_API_URL,
    NEXT_PUBLIC_NBCAMP_URL: process.env.NEXT_PUBLIC_NBCAMP_URL,
    NEXT_PUBLIC_KANT_URL: process.env.NEXT_PUBLIC_KANT_URL,
    NEXT_PUBLIC_CDN_BASE_URL: process.env.NEXT_PUBLIC_CDN_BASE_URL,

    // Client - 외부 서비스 키
    NEXT_PUBLIC_AMPLITUDE_KEY: process.env.NEXT_PUBLIC_AMPLITUDE_KEY,
    NEXT_PUBLIC_CHANNEL_IO_PLUGIN_KEY: process.env.NEXT_PUBLIC_CHANNEL_IO_PLUGIN_KEY,
    NEXT_PUBLIC_GTM_ID: process.env.NEXT_PUBLIC_GTM_ID,

    // Client - CPL 초기화 키
    NEXT_PUBLIC_FLOUNDER_KEY: process.env.NEXT_PUBLIC_FLOUNDER_KEY,
    NEXT_PUBLIC_HACKLE_SDK_KEY: process.env.NEXT_PUBLIC_HACKLE_SDK_KEY,
    NEXT_PUBLIC_BRAZE_API_KEY: process.env.NEXT_PUBLIC_BRAZE_API_KEY,
    NEXT_PUBLIC_BRAZE_SDK_ENDPOINT: process.env.NEXT_PUBLIC_BRAZE_SDK_ENDPOINT,

    // Client - 채널톡 이미지
    NEXT_PUBLIC_CHTALK_PC: process.env.NEXT_PUBLIC_CHTALK_PC,
    NEXT_PUBLIC_CHTALK_MO: process.env.NEXT_PUBLIC_CHTALK_MO,

    // Client - 썸네일 CloudFront 호스트
    NEXT_PUBLIC_CLOUDFRONT_HOSTNAME: process.env.NEXT_PUBLIC_CLOUDFRONT_HOSTNAME,

    // Client - Datadog RUM
    NEXT_PUBLIC_DATADOG_APPLICATION_ID: process.env.NEXT_PUBLIC_DATADOG_APPLICATION_ID,
    NEXT_PUBLIC_DATADOG_CLIENT_TOKEN: process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN,
    NEXT_PUBLIC_DATADOG_SERVICE: process.env.NEXT_PUBLIC_DATADOG_SERVICE,
    NEXT_PUBLIC_DATADOG_ENV: process.env.NEXT_PUBLIC_DATADOG_ENV,
    NEXT_PUBLIC_DATADOG_SESSION_RATE: process.env.NEXT_PUBLIC_DATADOG_SESSION_RATE,
    NEXT_PUBLIC_DATADOG_SESSION_REPLAY_RATE: process.env.NEXT_PUBLIC_DATADOG_SESSION_REPLAY_RATE,
    NEXT_PUBLIC_DATADOG_TRACE_SAMPLE_RATE: process.env.NEXT_PUBLIC_DATADOG_TRACE_SAMPLE_RATE,

    // Server
    GITHUB_PUBLIC_API_TOKEN: process.env.GITHUB_PUBLIC_API_TOKEN,
    UTM_COLLECTOR_AUTH_TOKEN: process.env.UTM_COLLECTOR_AUTH_TOKEN,
  },
  // 테스트에서는 skip해서 env 검증을 하지 않도록 설정
  skipValidation: process.env.NODE_ENV === 'test',
});
